import { useCallback, useEffect, useRef, useState } from 'react';
import { coursesApi } from '../services/coursesApi';
import { logger } from '../../../lib/logger';

/**
 * Knowledge Check hook - manages mid-lesson multiple choice quiz state
 *
 * Supports two initialization modes:
 * 1. Legacy: Call initialize() which fetches from /lessons/{id}/knowledge-check
 * 2. Unified: Pass initialData from useSectionView (pre-loaded from /sections/view)
 *
 * Flow:
 * 1. Initial state comes from either initialData or /knowledge-check GET endpoint
 * 2. Submit: POST /lessons/{id}/knowledge-check/submit → results with explanations
 *
 * Key differences from Quiz:
 * - Multiple choice (not free-form text)
 * - All questions shown at once
 * - 100% required to pass (but not required to proceed)
 * - Shows explanations after submission
 *
 * @param {{
 *   lessonId: string,
 *   initialData?: import('../types/sectionView.types').KnowledgeCheckContent | null,
 *   onCourseComplete?: () => void
 * }} params
 */
export function useKnowledgeCheck({ lessonId, initialData = null, onCourseComplete }) {
  // UI states
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'submitting' | 'results'
  const [error, setError] = useState(null);

  // Question data
  const [checkId, setCheckId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [previousAttempt, setPreviousAttempt] = useState(null);

  // User answers (index per question)
  const [answers, setAnswers] = useState([]);

  // Results after submission
  const [results, setResults] = useState(null);
  const [score, setScore] = useState(null);
  const [total, setTotal] = useState(null);
  const [passed, setPassed] = useState(false);

  // Refs to prevent double initialization
  const initRef = useRef(false);
  const hydratedCheckIdRef = useRef(null);

  // Hydrate state from initialData when it changes (unified endpoint mode)
  useEffect(() => {
    if (!initialData) return;

    // Only hydrate if this is a new check (different checkId)
    if (hydratedCheckIdRef.current === initialData.checkId) {
      return;
    }

    logger.log('[useKnowledgeCheck] Hydrating from initialData, checkId:', initialData.checkId);

    hydratedCheckIdRef.current = initialData.checkId;
    initRef.current = true; // Mark as initialized to prevent /knowledge-check call

    setCheckId(initialData.checkId);
    setQuestions(initialData.questions || []);
    setPreviousAttempt(initialData.previousAttempt || null);

    // Initialize answers - use previous attempt answers if available, otherwise -1
    const questionCount = initialData.questions?.length || 0;
    if (initialData.previousAttempt?.answers && Array.isArray(initialData.previousAttempt.answers)) {
      // Pre-populate with previous answers
      setAnswers(initialData.previousAttempt.answers);
      setScore(initialData.previousAttempt.score);
      setTotal(initialData.previousAttempt.total);
      setPassed(initialData.previousAttempt.passed);
    } else {
      // No previous attempt, start fresh
      setAnswers(new Array(questionCount).fill(-1));
      setScore(null);
      setTotal(null);
      setPassed(false);
    }

    setResults(null);
    setError(null);
    setStatus('ready');
  }, [initialData]);

  // Reset state when lesson changes (legacy mode without initialData)
  useEffect(() => {
    // Skip reset if we're using unified mode with initialData
    if (initialData) {
      return;
    }

    // Reset all state when lesson changes (legacy mode)
    logger.log('[useKnowledgeCheck] Resetting state for lessonId change (legacy mode)');
    setStatus('idle');
    setError(null);
    setCheckId(null);
    setQuestions([]);
    setPreviousAttempt(null);
    setAnswers([]);
    setResults(null);
    setScore(null);
    setTotal(null);
    setPassed(false);
    initRef.current = false;
    hydratedCheckIdRef.current = null;
  }, [lessonId, initialData]);

  // Fetch knowledge check questions (legacy mode - calls /knowledge-check)
  const initialize = useCallback(async () => {
    if (!lessonId) return;

    // Skip if already initialized (either from initialData or previous fetch)
    if (initRef.current) {
      logger.log('[useKnowledgeCheck] Already initialized, skipping fetch');
      return;
    }
    initRef.current = true;

    setStatus('loading');
    setError(null);

    try {
      logger.log('[useKnowledgeCheck] Fetching via /knowledge-check for lesson:', lessonId);

      const data = await coursesApi.fetchKnowledgeCheck({
        lessonId,
        auth: true,
      });

      setCheckId(data.checkId);
      setQuestions(data.questions || []);
      setPreviousAttempt(data.previousAttempt || null);

      // Initialize answers - use previous attempt answers if available, otherwise -1
      const questionCount = data.questions?.length || 0;
      if (data.previousAttempt?.answers && Array.isArray(data.previousAttempt.answers)) {
        // Pre-populate with previous answers
        setAnswers(data.previousAttempt.answers);
        setScore(data.previousAttempt.score);
        setTotal(data.previousAttempt.total);
        setPassed(data.previousAttempt.passed);
      } else {
        // No previous attempt, start fresh
        setAnswers(new Array(questionCount).fill(-1));
      }

      setStatus('ready');
    } catch (err) {
      console.error('[useKnowledgeCheck] Failed to fetch:', err);
      setError('Failed to load knowledge check. Please try again.');
      setStatus('idle');
      initRef.current = false;
    }
  }, [lessonId]);

  // Set answer for a specific question
  const setAnswer = useCallback((questionIndex, optionIndex) => {
    setAnswers(prev => {
      const next = [...prev];
      next[questionIndex] = optionIndex;
      return next;
    });
  }, []);

  // Check if all questions are answered
  const allAnswered = answers.length > 0 && answers.every(a => a >= 0);

  // Submit answers
  const submit = useCallback(async () => {
    if (!checkId || !allAnswered) return;
    if (status === 'submitting') return;

    setStatus('submitting');
    setError(null);

    try {
      logger.log('[useKnowledgeCheck] Submitting answers');

      const data = await coursesApi.submitKnowledgeCheck({
        lessonId,
        checkId,
        answers,
        auth: true,
      });

      logger.log('[useKnowledgeCheck] Submit response:', data);

      setResults(data.results || []);
      setScore(data.score);
      setTotal(data.total);
      setPassed(data.passed);
      setStatus('results');

      // Check if course was just completed
      if (data.courseJustCompleted && onCourseComplete) {
        onCourseComplete();
      }
    } catch (err) {
      console.error('[useKnowledgeCheck] Failed to submit:', err);
      setError('Failed to submit answers. Please try again.');
      setStatus('ready');
    }
    // onCourseComplete is a parent-passed callback; including it would
    // invalidate this callback on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkId, lessonId, answers, allAnswered, status]);

  // Reset to try again
  const retry = useCallback(() => {
    setAnswers(new Array(questions.length).fill(-1));
    setResults(null);
    setStatus('ready');
  }, [questions.length]);

  // Full reset (refetch)
  const reset = useCallback(() => {
    initRef.current = false;
    hydratedCheckIdRef.current = null;
    setStatus('idle');
    setError(null);
    setResults(null);
    initialize();
  }, [initialize]);

  return {
    // Status
    status,
    error,
    isLoading: status === 'loading',
    isSubmitting: status === 'submitting',
    isReady: status === 'ready',
    showResults: status === 'results',

    // Data
    checkId,
    questions,
    previousAttempt,

    // User input
    answers,
    setAnswer,
    allAnswered,

    // Results
    results,
    score,
    total,
    passed,

    // Actions
    initialize,
    submit,
    retry,
    reset,
  };
}
